import styled from '@emotion/styled';
import type { WidgetSnapshotEntry } from '@trinity/chat/utils/dashboard-widgets';
import { BasicMarkdown } from '@trinity/chat-components/components/Markdown/Markdown';

type Props = {
  entry: WidgetSnapshotEntry;
};

/**
 * A widget on a page-builder page shown from the compact entry the agent read, for the widgets that have no chart data
 * of their own to draw: a stat grid, a quote, a table preview, a text block. Charts are drawn from their data instead.
 */
export const PageWidgetEntry = ({ entry }: Props) => {
  switch (entry.kind) {
    case 'text':
      return (
        <Block>
          <Title>{entry.title}</Title>
          <BasicMarkdown>{entry.content}</BasicMarkdown>
        </Block>
      );
    case 'quote':
      return (
        <Block>
          <Title>{entry.title}</Title>
          <Quote>
            <p>{entry.content}</p>
            {entry.speaker && <Speaker>{entry.speaker}</Speaker>}
          </Quote>
        </Block>
      );
    case 'stat_grid':
      return (
        <Block>
          <Title>{entry.title}</Title>
          <StatGrid>
            {entry.items.map((item) => (
              <Stat key={`${item.label}:${item.value}`}>
                <StatValue>{`${item.value}${item.unit ?? ''}`}</StatValue>
                <StatLabel>{item.label}</StatLabel>
              </Stat>
            ))}
          </StatGrid>
        </Block>
      );
    case 'data_table':
      return (
        <Block>
          <Title>{entry.title}</Title>
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <th />
                  {entry.columns.map((column) => (<th key={column}>{column}</th>))}
                </tr>
              </thead>
              <tbody>
                {entry.previewRows.map((row, rowIndex) => (
                  <tr key={entry.rowLabels[rowIndex] ?? rowIndex}>
                    <th>{entry.rowLabels[rowIndex]}</th>
                    {row.map((cell, cellIndex) => (<td key={entry.columns[cellIndex] ?? cellIndex}>{cell ?? ''}</td>))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
          {entry.rowCount > entry.previewRows.length && (
            <Note>{`Showing ${entry.previewRows.length} of ${entry.rowCount} rows`}</Note>
          )}
        </Block>
      );
    case 'series_chart':
      return (
        <Block>
          <Title>{entry.title}</Title>
          <Note>Chart too large to preview. Use View to see it on the page.</Note>
        </Block>
      );
    case 'unsupported_widget_type':
      return (
        <Block>
          <Title>{entry.title}</Title>
          <Note>This widget type has no preview.</Note>
        </Block>
      );
    case 'no_data':
      return (
        <Block>
          <Title>{entry.title}</Title>
          <Note>This widget has no data.</Note>
        </Block>
      );
    default: {
      // A widget kind added to the snapshot without deciding here how it previews is a type error.
      const _exhaustive: never = entry;
      return null;
    }
  }
};

const Block = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: 12,
  border: '1px solid var(--gray-xl, #e0e0e0)',
  borderRadius: 8,
  backgroundColor: '#fff',
});

const Title = styled.h4(({ theme }) => ({
  fontSize: 14,
  fontWeight: 600,
  color: theme.palette.gray.dark1,
  margin: 0,
}));

const Quote = styled.blockquote({
  margin: 0,
  paddingLeft: 12,
  borderLeft: '3px solid var(--gray-xl, #e0e0e0)',
  fontStyle: 'italic',
  '& p': { margin: 0 },
});

const Speaker = styled.div(({ theme }) => ({
  marginTop: 4,
  fontStyle: 'normal',
  fontSize: 12,
  color: theme.palette.gray.dark1,
}));

const StatGrid = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
  gap: 12,
});

const Stat = styled.div({ display: 'flex', flexDirection: 'column', gap: 2 });

const StatValue = styled.div({ fontSize: 20, fontWeight: 600 });

const StatLabel = styled.div(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.gray.dark1,
}));

const TableScroll = styled.div({ width: '100%', overflowX: 'auto' });

const Table = styled.table({
  borderCollapse: 'collapse',
  fontSize: 12,
  '& th, & td': {
    padding: '4px 8px',
    textAlign: 'left',
    borderBottom: '1px solid var(--gray-xl, #e0e0e0)',
  },
});

const Note = styled.div(({ theme }) => ({
  fontSize: 12,
  fontStyle: 'italic',
  color: theme.palette.gray.dark1,
}));
